import { supabase } from '@/shared/config/supabase';
import { FeatureFlag } from '../../domain/entities/FeatureFlag';
import {
  IFeatureFlagRepository,
  CreateFeatureFlagData,
  UpdateFeatureFlagData,
} from '../../domain/repositories/IFeatureFlagRepository';

interface FeatureFlagRow {
  id: number;
  key: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export class SupabaseFeatureFlagRepository implements IFeatureFlagRepository {
  /**
   * Get all feature flags
   */
  async findAll(): Promise<FeatureFlag[]> {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch feature flags: ${error.message}`);
    }

    if (!data) {
      return [];
    }

    return data.map((row) => this.mapToEntity(row));
  }

  /**
   * Get a single feature flag by ID
   */
  async findById(flagId: number): Promise<FeatureFlag | null> {
    const { data, error } = await supabase
      .from('feature_flags')
      .select('*')
      .eq('id', flagId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      throw new Error(`Failed to fetch feature flag: ${error.message}`);
    }

    if (!data) {
      return null;
    }

    return this.mapToEntity(data);
  }

  /**
   * Create a new feature flag
   */
  async create(data: CreateFeatureFlagData): Promise<FeatureFlag> {
    const { data: row, error } = await supabase
      .from('feature_flags')
      .insert({
        key: data.key,
        name: data.name,
        description: data.description,
        is_active: data.isActive,
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create feature flag: ${error.message}`);
    }

    if (!row) {
      throw new Error('Failed to create feature flag: No data returned');
    }

    return this.mapToEntity(row);
  }

  /**
   * Update an existing feature flag via RPC to avoid CORS issues with PATCH
   */
  async update(
    flagId: number,
    updates: UpdateFeatureFlagData
  ): Promise<FeatureFlag> {
    const { data, error } = await supabase.rpc('toggle_feature_flag', {
      p_flag_id: flagId,
      p_is_active: updates.isActive ?? null,
      p_name: updates.name ?? null,
      p_description: updates.description ?? null,
    });

    if (error) {
      throw new Error(`Failed to update feature flag: ${error.message}`);
    }

    if (!data) {
      throw new Error('Failed to update feature flag: No data returned');
    }

    const row = data as unknown as FeatureFlagRow;
    return this.mapToEntity(row);
  }

  /**
   * Delete a feature flag (cascade handles junction table)
   */
  async delete(flagId: number): Promise<void> {
    const { error } = await supabase
      .from('feature_flags')
      .delete()
      .eq('id', flagId);

    if (error) {
      throw new Error(`Failed to delete feature flag: ${error.message}`);
    }
  }

  /**
   * Get all workspace IDs that have this flag enabled
   */
  async getWorkspaceIds(flagId: number): Promise<number[]> {
    const { data, error } = await supabase
      .from('workspace_feature_flags')
      .select('workspace_id')
      .eq('feature_flag_id', flagId);

    if (error) {
      throw new Error(`Failed to fetch workspace IDs: ${error.message}`);
    }

    if (!data) {
      return [];
    }

    return data.map((row) => row.workspace_id);
  }

  /**
   * Get workspace counts for all flags
   * Returns a map of flagId -> count
   */
  async getWorkspaceCounts(): Promise<Record<number, number>> {
    const { data, error } = await supabase
      .from('workspace_feature_flags')
      .select('feature_flag_id');

    if (error) {
      throw new Error(`Failed to fetch workspace counts: ${error.message}`);
    }

    if (!data) {
      return {};
    }

    // Count occurrences of each feature_flag_id
    const counts: Record<number, number> = {};
    for (const row of data) {
      const flagId = row.feature_flag_id;
      counts[flagId] = (counts[flagId] || 0) + 1;
    }

    return counts;
  }

  /**
   * Enable a feature flag for a specific workspace
   */
  async enableForWorkspace(
    flagId: number,
    workspaceId: number
  ): Promise<void> {
    const { error } = await supabase
      .from('workspace_feature_flags')
      .insert({
        workspace_id: workspaceId,
        feature_flag_id: flagId,
      })
      .select();

    if (error) {
      // Ignore duplicate key errors (already enabled)
      if (error.code === '23505') {
        return;
      }
      throw new Error(
        `Failed to enable feature flag for workspace: ${error.message}`
      );
    }
  }

  /**
   * Disable a feature flag for a specific workspace
   */
  async disableForWorkspace(
    flagId: number,
    workspaceId: number
  ): Promise<void> {
    const { error } = await supabase
      .from('workspace_feature_flags')
      .delete()
      .eq('workspace_id', workspaceId)
      .eq('feature_flag_id', flagId);

    if (error) {
      throw new Error(
        `Failed to disable feature flag for workspace: ${error.message}`
      );
    }
  }

  /**
   * Get all enabled feature flag keys for a workspace
   */
  async getEnabledFlagKeysForWorkspace(workspaceId: number): Promise<string[]> {
    // Step 1: Get feature flag IDs assigned to this workspace
    const { data: assignments, error: assignError } = await supabase
      .from('workspace_feature_flags')
      .select('feature_flag_id')
      .eq('workspace_id', workspaceId);

    if (assignError) {
      throw new Error(`Failed to fetch workspace flag assignments: ${assignError.message}`);
    }

    if (!assignments || assignments.length === 0) {
      return [];
    }

    const flagIds = assignments.map((row) => row.feature_flag_id);

    // Step 2: Get active flags matching those IDs
    const { data, error } = await supabase
      .from('feature_flags')
      .select('key')
      .eq('is_active', true)
      .in('id', flagIds);

    if (error) {
      throw new Error(`Failed to fetch enabled flags: ${error.message}`);
    }

    if (!data) {
      return [];
    }

    return data.map((row) => row.key);
  }

  /**
   * Map database row to FeatureFlag entity
   */
  private mapToEntity(row: FeatureFlagRow): FeatureFlag {
    return new FeatureFlag(
      row.id,
      row.key,
      row.name,
      row.description,
      row.is_active,
      new Date(row.created_at),
      new Date(row.updated_at)
    );
  }
}
